import { useCallback, useEffect, useRef, useState } from "react";
import type { HomeworkListResponse, HomeworkStateFilter, HomeworkSummary } from "@edu/shared";
import * as api from "../../../lib/api.ts";
import { listErrorText } from "../../../lib/list-error.ts";

/**
 * Загрузка страниц списка домашек курсором (4.3 фазы 2).
 *
 * ⚠️ Долистывание по кнопке, а не по прокрутке. На канале 128 кбит/с подгрузка
 * «сама» означает подгрузку в момент, когда человек просто проскроллил мимо, то
 * есть трафик и ожидание, о которых он не просил (5.2 фазы 2).
 *
 * ⚠️ Смена разреза или фильтра начинает список заново, а не дописывает: страницы
 * разных выборок склеивать нельзя, курсор от одной в другой не значит ничего.
 *
 * Отказ сети это ВИДИМОЕ состояние с кнопкой «Повторить» и повтором по событию
 * `online`, как на карточке работы: пустой экран без объяснения на плохом
 * канале читается как «работ нет», и это неправда.
 */

/**
 * Счётчики состояний по всей выборке, а не по приехавшей странице. Считает их
 * сервер, и не потому, что дорого считать здесь: страница курсорная и отдаёт
 * свежие работы, а просрочка живёт в старых, поэтому счётчик по приехавшим
 * строкам показывал бы ноль ровно там, где он нужен.
 */
export type HomeworkListCounts = NonNullable<HomeworkListResponse["counts"]>;

export interface HomeworkListView {
  /** `null` означает «первая страница ещё не приехала»: это не пустой список. */
  rows: HomeworkSummary[] | null;
  /** `null` означает «сервер счётчиков не прислал»: сегменты тогда без чисел. */
  counts: HomeworkListCounts | null;
  err: string | null;
  busy: boolean;
  /** `null` означает «дальше ничего нет»: кнопки «Показать ещё» тогда нет. */
  nextCursor: string | null;
  /** Список дочитан до конца: только тогда по нему можно судить о полноте. */
  complete: boolean;
  reload: () => void;
  loadMore: () => void;
}

function messageOf(e: unknown): string {
  return listErrorText(e, "Не вышло загрузить домашки. Попробуйте ещё раз.");
}

export function useHomeworkList(args: {
  studentUserId: string | null;
  state: HomeworkStateFilter;
}): HomeworkListView {
  const { studentUserId, state } = args;

  const [rows, setRows] = useState<HomeworkSummary[] | null>(null);
  const [counts, setCounts] = useState<HomeworkListCounts | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (cursor: string | null): Promise<void> => {
      setBusy(true);
      try {
        const res = await api.listHomework({ studentUserId, state, cursor });
        setRows((prev) => (cursor === null || prev === null ? res.homeworks : [...prev, ...res.homeworks]));
        // Счётчики едут только с первой страницей: фильтр от долистывания не
        // меняется, и повторять агрегат по всей истории на каждую страницу
        // значило бы платить за числа, которые уже показаны. Долистывание их
        // поэтому не трогает, иначе вторая страница стёрла бы то, что приехало
        // с первой
        if (cursor === null) setCounts(res.counts ?? null);
        setNextCursor(res.nextCursor ?? null);
        setErr(null);
      } catch (e) {
        setErr(messageOf(e));
      } finally {
        setBusy(false);
      }
    },
    [studentUserId, state],
  );

  useEffect(() => {
    setRows(null);
    setCounts(null);
    setNextCursor(null);
    void load(null);
  }, [load]);

  /**
   * Повтор сам по возврату сети. Через ref, а не через зависимость обработчика:
   * иначе слушатель переподписывался бы на каждую приехавшую страницу.
   */
  const retry = useRef<() => void>(() => {});
  useEffect(() => {
    retry.current = () => {
      if (err !== null) void load(null);
    };
  }, [err, load]);
  useEffect(() => {
    const wake = (): void => retry.current();
    window.addEventListener("online", wake);
    return () => window.removeEventListener("online", wake);
  }, []);

  return {
    rows,
    counts,
    err,
    busy,
    nextCursor,
    complete: rows !== null && nextCursor === null,
    reload: () => void load(null),
    loadMore: () => {
      if (nextCursor !== null && !busy) void load(nextCursor);
    },
  };
}
